<script setup lang="ts">
import { useExtendedI18n } from '@/i18n'
import { VSkeletonBar, VInfo } from '@/modules/shared/components'
import { format } from '@/modules/shared/utils/v-table'
import { toNumber, validMoney } from '@/modules/shared/utils/money'
import { Stat } from './types'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    rounded?: boolean | 'top' | 'bottom'
    skeleton?: boolean
    stats: Stat[]
    mobileCols?: 2 | 4
    precision?: number
    dark?: boolean
  }>(),
  {
    rounded: true,
    mobileCols: 4,
    precision: 2,
    dark: false,
  },
)

const { n } = useExtendedI18n()
const filteredStats = computed(() => props.stats.filter((stat) => stat.visible === undefined || stat.visible))
</script>

<template>
  <div
    class="grid divide-x"
    :class="[
      mobileCols === 2 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-4',
      {
        'rounded-lg': rounded === true,
        'rounded-t-lg': rounded === 'top',
        'rounded-b-lg': rounded === 'bottom',
      },
      dark
        ? 'divide-gray-700 bg-[#1E3448]'
        : 'divide-[var(--divider-color,#EEEFF0)] bg-white [box-shadow:var(--shadow-ring),var(--shadow-elevation)]',
    ]"
    v-if="filteredStats.length"
  >
    <div
      v-for="(stat, index) in filteredStats"
      :class="{
        'col-span-2': stat.colspan === 2,
        'col-span-3': stat.colspan === 3,
        'col-span-4': stat.colspan === 4,
      }"
      class="px-[var(--card-padding-x,1rem)] py-[var(--card-padding-y,0.75rem)]"
    >
      <div class="mb-1">
        <div
          class="flex items-center justify-between text-sm font-semibold"
          :class="dark ? 'text-gray-100' : 'text-gray-700'"
        >
          <div class="whitespace-nowrap">{{ stat.label }}</div>
          <VInfo v-if="stat.info">
            {{ stat.info }}
          </VInfo>
        </div>
        <div v-if="stat.sublabel" class="text-xs" :class="dark ? 'text-gray-400' : 'text-gray-500'">
          {{ stat.sublabel }}
        </div>
      </div>
      <div v-if="skeleton" class="text-2xl font-medium tracking-tight">
        <VSkeletonBar class="inline-block" :tone="dark ? 'dark' : undefined" />
      </div>
      <h3
        v-else
        class="text-xl font-medium tracking-tight sm:text-2xl"
        :class="[stat.color || (dark ? 'text-white' : 'text-gray-800')]"
      >
        <span
          v-if="stat.type === 'currency' && stat.value && validMoney(stat.value)"
          class="inline-flex items-end space-x-1"
        >
          <span v-if="!stat.hideSign && toNumber(stat.value) > 0" class="inline-block text-[#7abfb8]">+</span>
          <span v-if="!stat.hideSign && toNumber(stat.value) < 0" class="inline-block text-[#d16b6b]">-</span>
          <span>{{
            stat.hideSign
              ? n(stat.value, 'currency', null, stat.useCommonCurrency, precision)
              : n(stat.value, 'currency', null, stat.useCommonCurrency, precision)?.replace('-', '')
          }}</span>
        </span>

        <span v-else-if="stat.type === 'multiple'" class="inline-flex items-end space-x-1">
          <span>{{ format(stat.value, 'multiple') }}</span>
        </span>

        <span v-else-if="stat.type === 'percent'" class="inline-flex items-center space-x-1">
          <span
            v-if="stat.value && parseFloat((stat.value as number).toFixed(2)) >= 0"
            class="inline-block text-[#7abfb8]"
            >+</span
          >
          <span
            v-if="stat.value && parseFloat((stat.value as number).toFixed(2)) < 0"
            class="inline-block text-[#d16b6b]"
            >-</span
          >
          <span>
            {{
              (stat.value as number) < 0
                ? n(stat.value as number, 'percent', undefined, undefined, precision).substring(1)
                : n(stat.value as number, 'percent', undefined, undefined, precision)
            }}
          </span>
        </span>

        <span v-else class="inline-flex items-end space-x-1">{{ stat.value }}</span>
      </h3>
    </div>
  </div>
</template>
